import React, { useEffect, useState } from 'react'
import {
  Card,
  Descriptions,
  Tag,
  Button,
  Space,
  Image,
  Typography,
  Row,
  Col,
  Statistic,
  Rate,
  message,
  Spin,
  Alert,
} from 'antd'
import {
  EditOutlined,
  ArrowLeftOutlined,
  EnvironmentOutlined,
  PhoneOutlined,
  UserOutlined,
} from '@ant-design/icons'
import { useNavigate, useParams } from 'react-router-dom'
import { gardenService } from '@services/gardenService'
import { Garden } from '@types'
import dayjs from 'dayjs'

const { Title, Text, Paragraph } = Typography

const GardenDetail: React.FC = () => {
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()
  const [garden, setGarden] = useState<Garden | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (id) {
      loadGardenDetail(id)
    }
  }, [id])

  const loadGardenDetail = async (gardenId: string) => {
    setLoading(true)
    setError(null)
    try {
      const data = await gardenService.getGardenDetail(gardenId)
      setGarden(data)
    } catch (err: any) {
      setError(err?.message || '加载菜园详情失败')
      message.error('加载菜园详情失败')
    } finally {
      setLoading(false)
    }
  }

  const getStatusTag = (status: string) => {
    const statusMap = {
      approved: { color: 'green', text: '已发布' },
      pending: { color: 'orange', text: '未发布' },
      inactive: { color: 'red', text: '已下架' },
    }
    const config = statusMap[status as keyof typeof statusMap] || { color: 'default', text: status }
    return <Tag color={config.color}>{config.text}</Tag>
  }

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '100px 0' }}>
        <Spin size="large" />
      </div>
    )
  }

  if (error) {
    return (
      <Alert
        message="加载失败"
        description={error}
        type="error"
        showIcon
        action={
          <Button size="small" onClick={() => navigate('/gardens')}>
            返回列表
          </Button>
        }
      />
    )
  }

  if (!garden) {
    return (
      <Alert
        message="菜园不存在"
        description="未找到该菜园信息"
        type="warning"
        showIcon
        action={
          <Button size="small" onClick={() => navigate('/gardens')}>
            返回列表
          </Button>
        }
      />
    )
  }

  return (
    <div>
      <div style={{ marginBottom: '24px' }}>
        <Space>
          <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/gardens')}>
            返回
          </Button>
          <Button type="primary" icon={<EditOutlined />} onClick={() => navigate(`/gardens/edit/${garden.id}`)}>
            编辑
          </Button>
        </Space>
      </div>

      <Row gutter={[24, 24]}>
        <Col xs={24} lg={16}>
          <Card
            cover={
              <Image
                alt={garden.name}
                src={garden.imageUrl}
                fallback="https://placehold.co/600x400/EEEEEE/999999?text=菜园图片"
                style={{ height: '400px', objectFit: 'cover' }}
              />
            }
          >
            <div style={{ marginBottom: '16px' }}>
              <Title level={2} style={{ marginBottom: '8px' }}>
                {garden.name}
                {garden.isFeatured && <Tag color="gold" style={{ marginLeft: '8px' }}>精选</Tag>}
                {garden.isAd && <Tag color="red" style={{ marginLeft: '8px' }}>广告</Tag>}
              </Title>
              <Space>
                {getStatusTag(garden.status)}
                <Rate disabled value={garden.rating} style={{ fontSize: '16px' }} />
                <Text type="secondary">{garden.rating || 0}分</Text>
              </Space>
            </div>

            <Paragraph>{garden.description}</Paragraph>

            <Descriptions column={1}>
              <Descriptions.Item label="位置">
                <EnvironmentOutlined style={{ marginRight: '8px' }} />
                {garden.location}
              </Descriptions.Item>
              <Descriptions.Item label="农户信息">
                <Space>
                  <UserOutlined />
                  {garden.farmerName || '未设置'}
                  <PhoneOutlined />
                  {garden.phone || '未设置'}
                </Space>
              </Descriptions.Item>
              <Descriptions.Item label="特色标签">
                {garden.features?.map((feature, index) => (
                  <Tag key={index} color="blue">{feature}</Tag>
                ))}
              </Descriptions.Item>
              <Descriptions.Item label="订阅容量">
                {garden.currentSubscriptions || 0} / {garden.subscriptionCapacity || 0}
              </Descriptions.Item>
              <Descriptions.Item label="创建时间">
                {dayjs(garden.createTime || garden.createdAt).format('YYYY-MM-DD HH:mm:ss')}
              </Descriptions.Item>
              <Descriptions.Item label="更新时间">
                {dayjs(garden.updateTime || garden.updatedAt).format('YYYY-MM-DD HH:mm:ss')}
              </Descriptions.Item>
            </Descriptions>
          </Card>

          {garden.currentMenu && (
            <Card title="当前菜单" style={{ marginTop: '24px' }}>
              <Descriptions column={2}>
                <Descriptions.Item label="菜单名称">{garden.currentMenu.name}</Descriptions.Item>
                <Descriptions.Item label="季节">{garden.currentMenu.season}</Descriptions.Item>
                <Descriptions.Item label="描述" span={2}>
                  {garden.currentMenu.description}
                </Descriptions.Item>
              </Descriptions>

              {garden.currentMenu.vegetables && garden.currentMenu.vegetables.length > 0 && (
                <>
                  <Title level={5} style={{ marginTop: '16px' }}>蔬菜列表</Title>
                  <Row gutter={[16, 16]}>
                    {garden.currentMenu.vegetables.map((veg, index) => (
                      <Col key={index} xs={12} sm={8} md={6}>
                        <Card
                          size="small"
                          cover={
                            <Image
                              src={veg.imageUrl}
                              alt={veg.name}
                              fallback="https://placehold.co/100x100/EEEEEE/999999?text=蔬菜"
                            />
                          }
                        >
                          <Card.Meta description={veg.name} />
                        </Card>
                      </Col>
                    ))}
                  </Row>
                </>
              )}
            </Card>
          )}

          {garden.isAd && (
            <Card title="广告信息" style={{ marginTop: '24px' }}>
              <Descriptions column={1}>
                <Descriptions.Item label="广告标题">{garden.adTitle}</Descriptions.Item>
                <Descriptions.Item label="广告描述">{garden.adDescription}</Descriptions.Item>
                <Descriptions.Item label="广告链接">{garden.adLink}</Descriptions.Item>
              </Descriptions>
            </Card>
          )}
        </Col>

        <Col xs={24} lg={8}>
          <Card title="统计信息" style={{ marginBottom: '24px' }}>
            <Row gutter={[16, 16]}>
              <Col span={12}>
                <Statistic title="总营收" value={garden.totalRevenue || 0} prefix="¥" />
              </Col>
              <Col span={12}>
                <Statistic title="总订单" value={garden.totalOrders || 0} suffix="单" />
              </Col>
              <Col span={12}>
                <Statistic
                  title="当前订阅"
                  value={garden.currentSubscriptions || 0}
                  suffix={`/ ${garden.subscriptionCapacity || 0}`}
                />
              </Col>
              <Col span={12}>
                <Statistic title="评分" value={garden.rating || 0} suffix="/ 5.0" />
              </Col>
            </Row>
          </Card>

          <Card title="订阅套餐">
            {garden.subscriptionPlans?.monthly4 && (
              <Card
                type="inner"
                title="月付-配送4次/每周1次套餐"
                style={{ marginBottom: '16px' }}
                extra={
                  garden.subscriptionPlans.monthly4.enabled ? (
                    <Tag color="green">启用</Tag>
                  ) : (
                    <Tag>未启用</Tag>
                  )
                }
              >
                <Descriptions column={1} size="small">
                  <Descriptions.Item label="价格">
                    ¥{garden.subscriptionPlans.monthly4.userPrice}
                  </Descriptions.Item>
                  <Descriptions.Item label="配送次数">
                    {garden.subscriptionPlans.monthly4.deliveryCount}次/月
                  </Descriptions.Item>
                  <Descriptions.Item label="描述">
                    {garden.subscriptionPlans.monthly4.description}
                  </Descriptions.Item>
                </Descriptions>
              </Card>
            )}

            {garden.subscriptionPlans?.monthly8 && (
              <Card
                type="inner"
                title="月付-配送8次/每周2次套餐"
                extra={
                  garden.subscriptionPlans.monthly8.enabled ? (
                    <Tag color="green">启用</Tag>
                  ) : (
                    <Tag>未启用</Tag>
                  )
                }
              >
                <Descriptions column={1} size="small">
                  <Descriptions.Item label="价格">
                    ¥{garden.subscriptionPlans.monthly8.userPrice}
                  </Descriptions.Item>
                  <Descriptions.Item label="配送次数">
                    {garden.subscriptionPlans.monthly8.deliveryCount}次/月
                  </Descriptions.Item>
                  <Descriptions.Item label="描述">
                    {garden.subscriptionPlans.monthly8.description}
                  </Descriptions.Item>
                </Descriptions>
              </Card>
            )}
          </Card>
        </Col>
      </Row>
    </div>
  )
}

export default GardenDetail






